/**
 * Lab mapping-member.html — Doubtful / Watchlist by member name.
 */
import { Button, Field, Input, Modal, Spinner, useToast } from '@w7/ui'
import { useState } from 'react'
import type { BettickerMapping } from '@/api/cmApi'
import {
  useBettickerMapping,
  useUpdateBettickerMapping,
  type TickerMemberFlag,
} from '@/api/hooks/bet-ticker/useSmartMembers'
import { CheaterPlayerPicker } from '@/features/security/CheaterPlayerPicker'

export type MembersMappingModalProps = {
  open: boolean
  onClose: () => void
  /** Access Level — false = open modal read-only (no add/remove). */
  canEdit?: boolean
}

type Section = 'doubt' | 'watch'
/** Add spinner on Add btn; remove spinner on that chip’s ×. */
type MapBusy =
  | { op: 'add'; section: Section }
  | { op: 'remove'; section: Section; item: string }
  | null

function ChipList({
  items,
  flag,
  section,
  busy,
  canEdit,
  onRemove,
}: {
  items: string[]
  flag: Exclude<TickerMemberFlag, 'smart'>
  section: Section
  busy: MapBusy
  canEdit: boolean
  onRemove: (name: string) => void
}) {
  if (!items.length) return null
  const locked = !canEdit || busy != null
  const chipTone = flag === 'doubtfull' ? 'warn' : 'danger'
  return (
    <div className="w7-bm-smart-modal-selected">
      <span className="w7-bm-smart-modal-label">Selected:</span>
      <ul className="w7-bm-smart-chips">
        {items.map((name) => {
          const removing = busy?.op === 'remove' && busy.section === section && busy.item === name
          return (
            <li key={name} className={`w7-bm-smart-chip w7-bm-smart-chip--${chipTone}`}>
              <span>{name}</span>
              <button
                type="button"
                className="w7-bm-smart-chip-x"
                aria-label={removing ? `Removing ${name}` : `Remove ${name}`}
                aria-busy={removing}
                disabled={locked}
                onClick={() => onRemove(name)}
              >
                {removing ? <Spinner size={12} /> : '×'}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function MembersMappingModal({ open, onClose, canEdit = true }: MembersMappingModalProps) {
  const toast = useToast()
  const [doubtPlayer, setDoubtPlayer] = useState<string | null>(null)
  const [watchPlayer, setWatchPlayer] = useState<string | null>(null)
  const [busy, setBusy] = useState<MapBusy>(null)
  const mappingQ = useBettickerMapping(open)
  const updateM = useUpdateBettickerMapping()
  const mapping = mappingQ.data
  const pending = updateM.isPending

  const save = (
    next: Exclude<MapBusy, null>,
    patch: Partial<
      Pick<
        BettickerMapping,
        | 'mappedDoubtfullUsers'
        | 'mappedDoubtfullIps'
        | 'mappedWatchlistUsers'
        | 'mappedWatchlistIps'
      >
    >,
    okMsg: string,
    onOk?: () => void,
  ) => {
    if (!canEdit) return
    setBusy(next)
    updateM.mutate(patch, {
      onSuccess: () => {
        toast.success(okMsg)
        onOk?.()
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
      onSettled: () => setBusy(null),
    })
  }

  const addDoubt = () => {
    if (!canEdit) return
    const name = doubtPlayer?.trim()
    if (!name || !mapping || pending) return
    const list = mapping.mappedDoubtfullUsers
    if (list.some((u) => u.toLowerCase() === name.toLowerCase())) {
      toast.error('Member is already added')
      return
    }
    save(
      { op: 'add', section: 'doubt' },
      { mappedDoubtfullUsers: [...list, name] },
      'Mapping Member/IP Added',
      () => setDoubtPlayer(null),
    )
  }

  const addWatch = () => {
    if (!canEdit) return
    const name = watchPlayer?.trim()
    if (!name || !mapping || pending) return
    const list = mapping.mappedWatchlistUsers
    if (list.some((u) => u.toLowerCase() === name.toLowerCase())) {
      toast.error('Member is already added')
      return
    }
    save(
      { op: 'add', section: 'watch' },
      { mappedWatchlistUsers: [...list, name] },
      'Mapping Member/IP Added',
      () => setWatchPlayer(null),
    )
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (pending) return
        setDoubtPlayer(null)
        setWatchPlayer(null)
        onClose()
      }}
      title="Member Mapping"
      closeDisabled={pending}
    >
      <div className="w7-bm-smart-modal">
        {!canEdit ? (
          <p className="w7-bm-smart-modal-hint" data-testid="members-name-readonly">
            View only — Member&apos;s mapping Access Level is off for this page.
          </p>
        ) : null}
        <div className="w7-bm-map-section">
          <h4 className="w7-bm-map-heading">Doubtful Members</h4>
          <div className="w7-bm-smart-modal-add">
            <CheaterPlayerPicker
              value={doubtPlayer}
              onChange={canEdit ? setDoubtPlayer : () => undefined}
            />
            <Button
              type="button"
              variant="primary"
              busy={busy?.op === 'add' && busy.section === 'doubt'}
              disabled={!canEdit || !doubtPlayer?.trim() || pending}
              onClick={addDoubt}
            >
              Add
            </Button>
          </div>
          <ChipList
            items={mapping?.mappedDoubtfullUsers ?? []}
            flag="doubtfull"
            section="doubt"
            busy={busy}
            canEdit={canEdit}
            onRemove={(name) =>
              save(
                { op: 'remove', section: 'doubt', item: name },
                {
                  mappedDoubtfullUsers: (mapping?.mappedDoubtfullUsers ?? []).filter(
                    (u) => u !== name,
                  ),
                },
                'Mapping Member/IP Removed',
              )
            }
          />
        </div>

        <div className="w7-bm-map-section">
          <h4 className="w7-bm-map-heading">Watchlist Members</h4>
          <div className="w7-bm-smart-modal-add">
            <CheaterPlayerPicker
              value={watchPlayer}
              onChange={canEdit ? setWatchPlayer : () => undefined}
            />
            <Button
              type="button"
              variant="primary"
              busy={busy?.op === 'add' && busy.section === 'watch'}
              disabled={!canEdit || !watchPlayer?.trim() || pending}
              onClick={addWatch}
            >
              Add
            </Button>
          </div>
          <ChipList
            items={mapping?.mappedWatchlistUsers ?? []}
            flag="watchlist"
            section="watch"
            busy={busy}
            canEdit={canEdit}
            onRemove={(name) =>
              save(
                { op: 'remove', section: 'watch', item: name },
                {
                  mappedWatchlistUsers: (mapping?.mappedWatchlistUsers ?? []).filter(
                    (u) => u !== name,
                  ),
                },
                'Mapping Member/IP Removed',
              )
            }
          />
        </div>
      </div>
    </Modal>
  )
}

/** Lab mapping-ip.html — Doubtful / Watchlist by IP. */
export function MembersIpMappingModal({ open, onClose, canEdit = true }: MembersMappingModalProps) {
  const toast = useToast()
  const [doubtIp, setDoubtIp] = useState('')
  const [watchIp, setWatchIp] = useState('')
  const [busy, setBusy] = useState<MapBusy>(null)
  const mappingQ = useBettickerMapping(open)
  const updateM = useUpdateBettickerMapping()
  const mapping = mappingQ.data
  const pending = updateM.isPending

  const save = (
    next: Exclude<MapBusy, null>,
    patch: Partial<Pick<BettickerMapping, 'mappedDoubtfullIps' | 'mappedWatchlistIps'>>,
    okMsg: string,
    onOk?: () => void,
  ) => {
    if (!canEdit) return
    setBusy(next)
    updateM.mutate(patch, {
      onSuccess: () => {
        toast.success(okMsg)
        onOk?.()
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
      onSettled: () => setBusy(null),
    })
  }

  const addDoubt = () => {
    if (!canEdit) return
    const ip = doubtIp.trim()
    if (!ip || !mapping || pending) return
    if (mapping.mappedDoubtfullIps.some((u) => u.toLowerCase() === ip.toLowerCase())) {
      toast.error('IP is already added')
      return
    }
    save(
      { op: 'add', section: 'doubt' },
      { mappedDoubtfullIps: [...mapping.mappedDoubtfullIps, ip] },
      'Mapping Member/IP Added',
      () => setDoubtIp(''),
    )
  }

  const addWatch = () => {
    if (!canEdit) return
    const ip = watchIp.trim()
    if (!ip || !mapping || pending) return
    if (mapping.mappedWatchlistIps.some((u) => u.toLowerCase() === ip.toLowerCase())) {
      toast.error('IP is already added')
      return
    }
    save(
      { op: 'add', section: 'watch' },
      { mappedWatchlistIps: [...mapping.mappedWatchlistIps, ip] },
      'Mapping Member/IP Added',
      () => setWatchIp(''),
    )
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (pending) return
        setDoubtIp('')
        setWatchIp('')
        onClose()
      }}
      title="Member IP Mapping"
      closeDisabled={pending}
    >
      <div className="w7-bm-smart-modal">
        {!canEdit ? (
          <p className="w7-bm-smart-modal-hint" data-testid="members-ip-readonly">
            View only — Member&apos;s IP mapping Access Level is off for this page.
          </p>
        ) : null}
        <div className="w7-bm-map-section">
          <h4 className="w7-bm-map-heading">Doubtful IPs</h4>
          <div className="w7-bm-smart-modal-add">
            <Field label="IP address">
              {({ id }) => (
                <Input
                  id={id}
                  placeholder="e.g. 1.2.3.4"
                  value={doubtIp}
                  disabled={!canEdit}
                  onChange={(e) => setDoubtIp(e.target.value)}
                />
              )}
            </Field>
            <Button
              type="button"
              variant="primary"
              busy={busy?.op === 'add' && busy.section === 'doubt'}
              disabled={!canEdit || !doubtIp.trim() || pending}
              onClick={addDoubt}
            >
              Add
            </Button>
          </div>
          <ChipList
            items={mapping?.mappedDoubtfullIps ?? []}
            flag="doubtfull"
            section="doubt"
            busy={busy}
            canEdit={canEdit}
            onRemove={(ip) =>
              save(
                { op: 'remove', section: 'doubt', item: ip },
                {
                  mappedDoubtfullIps: (mapping?.mappedDoubtfullIps ?? []).filter((u) => u !== ip),
                },
                'Mapping Member/IP Removed',
              )
            }
          />
        </div>

        <div className="w7-bm-map-section">
          <h4 className="w7-bm-map-heading">Watchlist IPs</h4>
          <div className="w7-bm-smart-modal-add">
            <Field label="IP address">
              {({ id }) => (
                <Input
                  id={id}
                  placeholder="e.g. 1.2.3.4"
                  value={watchIp}
                  disabled={!canEdit}
                  onChange={(e) => setWatchIp(e.target.value)}
                />
              )}
            </Field>
            <Button
              type="button"
              variant="primary"
              busy={busy?.op === 'add' && busy.section === 'watch'}
              disabled={!canEdit || !watchIp.trim() || pending}
              onClick={addWatch}
            >
              Add
            </Button>
          </div>
          <ChipList
            items={mapping?.mappedWatchlistIps ?? []}
            flag="watchlist"
            section="watch"
            busy={busy}
            canEdit={canEdit}
            onRemove={(ip) =>
              save(
                { op: 'remove', section: 'watch', item: ip },
                {
                  mappedWatchlistIps: (mapping?.mappedWatchlistIps ?? []).filter((u) => u !== ip),
                },
                'Mapping Member/IP Removed',
              )
            }
          />
        </div>
      </div>
    </Modal>
  )
}
